import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { createContext, type ReactNode, type RefObject, useContext } from "react";

import { t } from "../i18n/i18n";
import { DETAIL_MAX, openFeedback } from "./feedbackModel";

/** « Signaler cette anomalie » sous un message d'erreur (registre F39) : encadré et bandeau d'erreur, avis d'erreur,
 *  carte d'incident d'affichage, état d'erreur d'une vue. Le lien ouvre la fenêtre de l'anomalie remplie d'avance :
 *  « Ce qui s'est passé » reprend le texte du message tel qu'il est affiché ; la vue (message compris) est copiée
 *  pour la capture, le contexte technique joint comme d'habitude.
 *
 *  Le lien n'apparaît que dans l'espace de travail et l'accueil (`ReportLinks enabled`, posé par `App`) : ni sur
 *  l'écran d'activation de la licence ou de connexion (la fenêtre des retours n'y est pas montée), ni dans les
 *  fenêtres des retours elles-mêmes (`ReportLinks enabled={false}`). */

const ReportContext = createContext(false);

export function ReportLinks({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  return <ReportContext.Provider value={enabled}>{children}</ReportContext.Provider>;
}

export function useReportLinks(): boolean {
  return useContext(ReportContext);
}

/** Texte affiché d'un message, sans le libellé de ses boutons (« Réessayer », le lien lui-même), espaces resserrés,
 *  borné à la taille du champ « Ce qui s'est passé ». */
export function shownText(element: Element | null | undefined): string {
  if (!element) return "";
  const read = (el: Element) => (el as HTMLElement).innerText ?? el.textContent ?? "";
  let raw = read(element);
  for (const button of Array.from(element.querySelectorAll("button"))) {
    const label = read(button).trim();
    if (label) raw = raw.replace(label, " ");
  }
  return raw.replace(/\s+/g, " ").trim().slice(0, DETAIL_MAX);
}

/** Lien « Signaler cette anomalie » : `source` est l'élément dont le texte affiché devient « ce qui s'est passé ». */
export function ReportAnomalyLink(props: { source: RefObject<Element | null>; className?: string }) {
  return useReportLinks() ? <ReportButton {...props} /> : null;
}

function ReportButton({ source, className }: { source: RefObject<Element | null>; className?: string }) {
  const queryClient = useQueryClient();
  return (
    <button
      type="button"
      onClick={() => openFeedback("anomaly", queryClient, { happened: shownText(source.current) })}
      className={clsx(
        "inline-flex items-center rounded-sm text-meta leading-snug font-medium text-accent underline-offset-2 transition-colors hover:underline",
        className,
      )}
    >
      {t("Signaler cette anomalie")}
    </button>
  );
}
